import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { PureComponent } from "react";

import { freeTextValidation, numberValidation, whiteSpaceValidation } from "@/app-utils/validation.ts";
import { type Attribute, type VocabularyTerm } from "@/graphql.ts";
import { getAttributeLabel } from "@/redux/modules/sample-admin.ts";
import { ATTRIBUTE_TYPE, REPRESENTER_COLUMN } from "@/settings/settings.js";

import {
  CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_NUMBER,
  CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_TEXT,
  CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_VOC_TERM,
  CONFIGURE_PARENT_TYPE_ATTRIBUTE_TEMPLATE,
} from "../../admin/ConfigureParentTypeAttributeDialog.js";
import { SCRIPTING_MACRO } from "../forms/ScriptInputField.js";
import InputField, { InputReduxFormField } from "./InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "./ReactSelectField.js";
import { ScriptInputReduxFormField } from "./ScriptInputField.js";

export interface IParentAttributeConfigurationFieldsProps {
  fieldName: string;
  attribute: Attribute;
  templateAttributes: Attribute[];
}

class ParentAttributeConfigurationFields extends PureComponent<IParentAttributeConfigurationFieldsProps> {
  constructor(props: IParentAttributeConfigurationFieldsProps) {
    super(props);
  }

  validateNumber = (value: string): string => {
    if (!value || value.trim().length === 0) {
      return undefined;
    }
    return numberValidation(value, this.props.attribute);
  };

  validateFreeText = (value: string): string => {
    if (!value || value.trim().length === 0) {
      return undefined;
    }
    return whiteSpaceValidation(value) || freeTextValidation(value, this.props.attribute);
  };

  render() {
    const attribute: Attribute = this.props.attribute;
    const fieldName: string = this.props.fieldName;

    // TODO: everything inside is mostly identical with ConfigureSampleTypeAttributeDialog - refactor?
    return (
      <div key={`defaults-${attribute.id}`}>
        <div style={{ marginTop: "5px" }}>
          <strong>{getAttributeLabel(attribute)}</strong>
        </div>
        {attribute.representedColumn !== REPRESENTER_COLUMN.NAME &&
          attribute.attributeType.id === ATTRIBUTE_TYPE.FREE_TEXT && (
            <InputReduxFormField
              componentClass="input"
              name={`${fieldName}.${CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_TEXT}`}
              label={"Default text"}
              placeholder={`Enter default text`}
              // maxLength can not be 0
              maxLength={attribute.maxLength || DB_FIELD_MAX_LENGTH.ATTRIBUTE.DEFAULT_TEXT}
              component={InputField}
              validate={this.validateFreeText}
            />
          )}

        {attribute.attributeType.id === ATTRIBUTE_TYPE.FREE_TEXT && (
          <ScriptInputReduxFormField
            name={`${fieldName}.${CONFIGURE_PARENT_TYPE_ATTRIBUTE_TEMPLATE}`}
            title={"Template"}
            maxLength={DB_FIELD_MAX_LENGTH.ATTRIBUTE.TEMPLATE}
            attributes={this.props.templateAttributes}
            acceptedMacros={[SCRIPTING_MACRO.DATE, SCRIPTING_MACRO.PARENT_TYPE]}
          />
        )}

        {attribute.attributeType.id === ATTRIBUTE_TYPE.FREE_NUMERICAL && (
          <InputReduxFormField
            componentClass="input"
            type="number"
            name={`${fieldName}.${CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_NUMBER}`}
            label={"Default number"}
            placeholder={`Enter default number`}
            component={InputField}
            validate={this.validateNumber}
          />
        )}

        {attribute.attributeType.id === ATTRIBUTE_TYPE.CONTROLLED_TEXT && (
          <ReactSelectReduxFormField
            name={`${fieldName}.${CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_VOC_TERM}`}
            label={"Default term"}
            placeholder="Select a default..."
            component={ReactSelectField}
            options={attribute.vocabularyTerms}
            isSearchable
            closeMenuOnSelect
            openMenuOnFocus
            isClearable={true}
            isMulti={false}
            getOptionValue={(option: VocabularyTerm) => option.id}
            getOptionLabel={(option: VocabularyTerm) => option.name}
          />
        )}

        <hr />
      </div>
    );
  }
}

export default ParentAttributeConfigurationFields;
